import React, { useEffect, useState } from 'react';
import { RefreshCw, Database, Zap, Bot, ListChecks, Webhook, BarChart2, Landmark, PlayCircle, ShieldCheck, Cog } from 'lucide-react';
import { useT } from '../../i18n';
import AdminPixelsPanel from './AdminPixelsPanel';
import { authFetch } from '../../lib/auth';
import { useOs2, PageHead, Card, timeAgo, Fade, RangeNotice } from './os2ui';

/* System Health — the single source of truth for the whole platform. Admin-only. */

const PILL = {
  ok: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  online: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  configured: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  idle: 'bg-sky-50 text-sky-700 border-sky-200',
  external: 'bg-sky-50 text-sky-700 border-sky-200',
  tracking_active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  stale: 'bg-amber-50 text-amber-700 border-amber-200',
  degraded: 'bg-amber-50 text-amber-700 border-amber-200',
  no_heartbeat_yet: 'bg-gray-50 text-gray-500 border-gray-200',
  not_configured: 'bg-gray-50 text-gray-500 border-gray-200',
  unknown: 'bg-gray-50 text-gray-500 border-gray-200',
  down: 'bg-red-50 text-red-700 border-red-200',
  error: 'bg-red-50 text-red-700 border-red-200',
};
const DOT = { ok: '🟢', online: '🟢', active: '🟢', configured: '🟢', tracking_active: '🟢', idle: '🔵', external: '🔵', stale: '🟠', pending: '🟠', degraded: '🟠', no_heartbeat_yet: '⚪', not_configured: '⚪', unknown: '⚪', down: '🔴', error: '🔴' };

function Pill({ status }) {
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${PILL[status] || PILL.unknown}`}>{DOT[status] || '⚪'} {status?.replace(/_/g, ' ')}</span>;
}

function SectionTitle({ icon: Icon, children }) {
  return <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-2 mt-6"><Icon className="w-4 h-4 text-[#F77F11]" /> {children}</p>;
}

const scoreColor = (s) => (s >= 85 ? 'text-emerald-600' : s >= 65 ? 'text-amber-600' : 'text-red-600');

const STEP_STYLE = {
  pass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  fail: 'bg-red-50 text-red-700 border-red-300',
  guarded: 'bg-sky-50 text-sky-700 border-sky-200',
  skipped: 'bg-gray-50 text-gray-400 border-gray-200',
};
const STEP_ICON = { pass: '✓', fail: '✕', guarded: '🛡', skipped: '—' };

function PreflightSection({ isEs }) {
  const [last, setLast] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => authFetch('/os2/preflight/last').then((r) => r.json()).then((j) => setLast(j.last)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const r = await authFetch('/os2/preflight/run', { method: 'POST' });
      const j = await r.json();
      if (r.status === 429) { setLast((p) => p); alert(isEs ? 'Espera 30s entre ejecuciones (cada una toca Brevo real).' : 'Wait 30s between runs (each hits real Brevo).'); return; }
      setLast(j);
    } finally { setBusy(false); }
  };
  return (
    <Card className="p-5 mt-3" data-testid="health-preflight">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
            <ShieldCheck className="w-4 h-4 text-[#F77F11]" /> Preflight Protocol
            {last && (
              <span className={`rounded-full border px-2 py-0.5 text-[10.5px] font-bold ${last.overall === 'pass' ? STEP_STYLE.pass : STEP_STYLE.fail}`} data-testid="preflight-overall">
                {last.overall === 'pass' ? 'PASS' : 'FAIL'}
              </span>
            )}
          </p>
          <p className="text-[12px] text-gray-400 mt-0.5">
            {isEs ? 'Un lead sintético (protegido por el email-guard, auto-limpiado) recorre TODA la cadena de producción con PASS/FAIL por paso.'
              : 'A synthetic lead (email-guarded, auto-cleaned) travels the ENTIRE production chain with PASS/FAIL per step.'}
            {last && <> · {isEs ? 'Última ejecución' : 'Last run'}: {timeAgo(last.at, isEs)} · {last.took_ms} ms</>}
          </p>
        </div>
        <button onClick={run} disabled={busy} data-testid="preflight-run-btn"
          className="inline-flex items-center gap-2 rounded-full bg-gray-900 hover:bg-[#F77F11] text-white text-[12.5px] font-semibold px-4 py-2 transition-colors disabled:opacity-50">
          <PlayCircle className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> {busy ? (isEs ? 'Validando…' : 'Validating…') : (isEs ? 'Ejecutar Preflight' : 'Run Preflight')}
        </button>
      </div>
      {last && (
        <div className="mt-4 flex flex-wrap items-center gap-y-2" data-testid="preflight-steps">
          {last.steps.map((s, i) => (
            <React.Fragment key={s.id}>
              {i > 0 && <span className="mx-1.5 text-gray-300">→</span>}
              <span title={s.detail} data-testid={`preflight-step-${s.id}`}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11.5px] font-semibold cursor-default ${STEP_STYLE[s.status] || STEP_STYLE.skipped}`}>
                {STEP_ICON[s.status] || '—'} {isEs ? s.label_es : s.label_en}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}
      {last && (
        <p className="mt-3 text-[10.5px] text-gray-400">
          🛡 = {isEs ? 'protegido por email-guard (los datos sintéticos no tocan CRM/Brevo real, por diseño)' : 'email-guarded (synthetic data never touches real CRM/Brevo, by design)'} ·
          — = {isEs ? 'no aplicable en esta ejecución' : 'not applicable in this run'}
        </p>
      )}
      {!last && <p className="mt-3 text-[12.5px] text-gray-400">{isEs ? 'Aún no se ha ejecutado ningún preflight.' : 'No preflight has been run yet.'}</p>}
    </Card>
  );
}

const VAL_ICON = { validated: '✅', partial: '🟡', pending: '🟡', blocked: '🔴', failed: '🔴' };

const PRIO = {
  critical: 'bg-red-100 text-red-800 border-red-300',
  high: 'bg-red-50 text-red-700 border-red-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-sky-50 text-sky-700 border-sky-200',
};

function EvaCooSection({ isEs }) {
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => authFetch('/os2/eva/coo-report').then((r) => r.json()).then(setRep).catch((e) => setErr(String(e)));
  useEffect(() => { load(); }, []);
  const F = ({ l, v }) => v ? <p className="text-[12px] text-gray-600"><b className="text-gray-800">{l}:</b> {v}</p> : null;
  return (
    <Card className="p-5 mt-3" data-testid="health-eva-coo">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
            <Bot className="w-4 h-4 text-[#F77F11]" /> Eva · COO Operating Report
            {rep && <span className={`rounded-full border px-2 py-0.5 text-[10.5px] font-bold ${rep.production_ready ? PILL.ok : PILL.degraded}`} data-testid="coo-status-pill">{rep.current_status}</span>}
          </p>
          <p className="text-[12px] text-gray-400 mt-0.5">
            {isEs ? 'Eva no reporta errores: mantiene la empresa operativa. Auto-cura lo seguro y solo escala lo que requiere intervención humana.'
              : 'Eva does not report errors: she keeps the company operational. Self-heals what is safe and only escalates what needs a human.'}
          </p>
        </div>
        <button onClick={load} data-testid="coo-refresh-btn" className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 hover:border-[#F77F11] px-3 py-1.5 text-[12px] font-semibold text-gray-600 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> {isEs ? 'Actualizar' : 'Refresh'}
        </button>
      </div>
      {err && <p className="mt-3 text-[12.5px] text-red-600">{err}</p>}
      {rep && (
        <>
          <p className="mt-3 text-[13.5px] font-semibold text-gray-800" data-testid="coo-headline">{rep.headline}</p>
          {(rep.open_incidents || []).length > 0 ? (
            <div className="mt-3 space-y-3" data-testid="coo-incidents">
              {rep.open_incidents.map((i) => (
                <div key={i.key} data-testid={`coo-incident-${i.key.replace(/[^a-z0-9]+/gi, '-')}`}
                  className={`rounded-xl border-l-4 bg-gray-50/60 border border-gray-100 p-4 ${i.status === 'escalated' ? 'border-l-red-500' : i.priority === 'critical' || i.priority === 'high' ? 'border-l-red-400' : i.priority === 'medium' ? 'border-l-amber-400' : 'border-l-sky-400'}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-[13.5px] font-bold text-gray-900 flex-1">{i.component}</p>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${PRIO[i.priority] || PRIO.medium}`}>{i.priority}</span>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${i.status === 'escalated' ? PILL.error : PILL.degraded}`}>{i.status}</span>
                  </div>
                  <div className="mt-2 space-y-1">
                    <F l={isEs ? 'Problema' : 'Problem'} v={i.problem} />
                    <F l={isEs ? 'Causa' : 'Cause'} v={i.cause} />
                    <F l={isEs ? 'Impacto' : 'Impact'} v={i.impact} />
                    <F l={isEs ? 'Acción automática' : 'Auto action'} v={i.auto_action} />
                    <F l={isEs ? 'Acción recomendada' : 'Recommended action'} v={i.recommended_action} />
                    <F l={isEs ? 'Próximo paso' : 'Next step'} v={i.next_step} />
                    <p className="text-[10.5px] text-gray-400">{isEs ? 'Abierto' : 'Opened'} {timeAgo(i.opened_at, isEs)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-[13px] text-emerald-600 font-semibold" data-testid="coo-no-incidents">
              ✓ {isEs ? 'Sin incidentes abiertos — ninguna acción requerida por el momento.' : 'No open incidents — no action required at this time.'}
            </p>
          )}
          {(rep.self_healed || []).length > 0 && (
            <div className="mt-4" data-testid="coo-self-healed">
              <p className="text-[10.5px] uppercase tracking-wider text-emerald-600 font-bold">{isEs ? 'Auto-curación (este ciclo)' : 'Self-healing (this cycle)'}</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {rep.self_healed.map((s, k) => <span key={k} className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-2.5 py-1 text-[11px] font-medium">🛠 {s}</span>)}
              </div>
            </div>
          )}
          {(rep.recently_resolved || []).length > 0 && (
            <details className="mt-3">
              <summary className="text-[11.5px] text-gray-400 cursor-pointer">{isEs ? `Incidentes resueltos recientemente (${rep.recently_resolved.length})` : `Recently resolved incidents (${rep.recently_resolved.length})`}</summary>
              <div className="mt-2 space-y-1">
                {rep.recently_resolved.map((i) => (
                  <p key={i._id} className="text-[11.5px] text-gray-500">✓ <b>{i.component}</b> — {i.resolution} · {timeAgo(i.resolved_at, isEs)}</p>
                ))}
              </div>
            </details>
          )}
        </>
      )}
      {!rep && !err && <p className="mt-3 text-[12.5px] text-gray-400">{isEs ? 'Eva está revisando la operación…' : 'Eva is reviewing operations…'}</p>}
    </Card>
  );
}

const CRM_PROD = {
  VERIFIED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  DEGRADED: 'bg-amber-50 text-amber-700 border-amber-200',
  FAILED: 'bg-red-50 text-red-700 border-red-300',
};

function CrmSyncSection({ isEs }) {
  const [s, setS] = useState(null);
  const load = () => authFetch('/crm/sync/status').then((r) => r.json()).then(setS).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!s) return null;
  const M = ({ l, v, warn }) => (
    <p className="text-[12px] text-gray-600">{l}: <b className={`tabular-nums ${warn ? 'text-amber-600' : 'text-gray-800'}`}>{v ?? '—'}</b></p>
  );
  return (
    <Card className="p-5 mt-3" data-testid="health-crm-sync">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
          <Database className="w-4 h-4 text-[#F77F11]" /> CRM Sync Engine (Marblism → Kitxens → Brevo)
          <span className={`rounded-full border px-2 py-0.5 text-[10.5px] font-bold ${CRM_PROD[s.production_status] || CRM_PROD.DEGRADED}`} data-testid="crm-sync-status">{s.production_status}</span>
        </p>
        <button onClick={load} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 hover:border-[#F77F11] px-3 py-1.5 text-[12px] font-semibold text-gray-600 transition-colors" data-testid="crm-sync-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> {isEs ? 'Actualizar' : 'Refresh'}
        </button>
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-1.5" data-testid="crm-sync-metrics">
        <M l={isEs ? 'Sincronizados (total)' : 'Synced (total)'} v={s.synced_total} />
        <M l={isEs ? 'Sincronizados 24h' : 'Synced 24h'} v={s.synced_24h} />
        <M l={isEs ? 'Pendientes' : 'Pending'} v={s.pending_sync} warn={s.pending_sync > 0} />
        <M l={isEs ? 'Reintentando' : 'Retrying'} v={s.retrying} warn={s.retrying > 0} />
        <M l="DLQ" v={s.dlq_count} warn={s.dlq_count > 0} />
        <M l={isEs ? 'Fallidos 24h' : 'Failed 24h'} v={s.failed_24h} warn={s.failed_24h > 0} />
        <M l={isEs ? 'Duplicados evitados' : 'Duplicates prevented'} v={s.duplicate_prevention_events} />
        <M l={isEs ? 'Éxito en reintentos' : 'Retry success'} v={s.retry_success_rate_pct != null ? `${s.retry_success_rate_pct}%` : '—'} />
        <M l="Brevo API" v={s.brevo_api} warn={s.brevo_api !== 'ok'} />
        <M l={isEs ? 'Último sync OK' : 'Last successful sync'} v={s.last_successful_sync ? timeAgo(s.last_successful_sync, isEs) : '—'} />
        <M l={isEs ? 'Pendiente más antiguo' : 'Oldest pending'} v={s.oldest_pending ? timeAgo(s.oldest_pending, isEs) : '—'} />
        <M l={isEs ? 'Tasa de fallo' : 'Failure rate'} v={`${s.failure_rate_pct}%`} warn={s.failure_rate_pct > 5} />
      </div>
    </Card>
  );
}

function OpsSection({ isEs }) {
  const [d, setD] = useState(null);
  useEffect(() => { authFetch('/os2/operations').then((r) => r.json()).then(setD).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <Card className="p-5 mt-3" data-testid="health-ops">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
        <Cog className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Colas y errores recientes' : 'Queues & recent errors'}
        <span className={`rounded-full border px-2 py-0.5 text-[10.5px] font-bold ${d.overall === 'operational' ? PILL.ok : PILL.error}`}>
          {d.overall === 'operational' ? (isEs ? 'TODO OPERATIVO' : 'ALL OPERATIONAL') : (isEs ? 'ACCIÓN REQUERIDA' : 'ACTION REQUIRED')}
        </span>
      </p>
      <div className="mt-3 grid lg:grid-cols-2 gap-4">
        <div className="flex flex-wrap gap-2" data-testid="ops-queues">
          <div className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-2.5">
            <p className="text-lg font-semibold tabular-nums leading-none">{d.queues?.events_24h ?? 0}</p>
            <p className="text-[10.5px] text-gray-400 mt-1">{isEs ? 'eventos 24h' : 'events 24h'}</p>
          </div>
          <div className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-2.5">
            <p className="text-lg font-semibold tabular-nums leading-none">{d.queues?.crm_retrying ?? 0}</p>
            <p className="text-[10.5px] text-gray-400 mt-1">{isEs ? 'CRM reintentos' : 'CRM retrying'}</p>
          </div>
          <div className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-2.5">
            <p className={`text-lg font-semibold tabular-nums leading-none ${d.queues?.crm_dlq ? 'text-red-600' : ''}`}>{d.queues?.crm_dlq ?? 0}</p>
            <p className="text-[10.5px] text-gray-400 mt-1">CRM DLQ</p>
          </div>
        </div>
        <div data-testid="ops-errors">
          {(d.errors || []).length ? d.errors.slice(0, 5).map((e, i) => (
            <p key={i} className="text-[12px] text-gray-600 py-1 border-b border-gray-100 last:border-0">
              <span className="text-red-500 font-medium">{e.type}</span>
              {e.detail && <span className="text-gray-500"> · {String(e.detail).slice(0, 80)}</span>}
              <span className="text-gray-300"> · {timeAgo(e.at, isEs)}</span>
            </p>
          )) : <p className="text-[12.5px] text-emerald-600 font-semibold">✓ {isEs ? 'Sin errores registrados' : 'No errors on record'}</p>}
        </div>
      </div>
    </Card>
  );
}

const REG_CHIP = {
  pass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  fail: 'bg-red-50 text-red-700 border-red-300',
  guarded: 'bg-sky-50 text-sky-700 border-sky-200',
};

function RegressionSection({ isEs }) {
  const [last, setLast] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => authFetch('/os2/regression/last').then((r) => r.json()).then((j) => setLast(j.last)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const r = await authFetch('/os2/regression/run', { method: 'POST' });
      if (r.status === 429) { alert(isEs ? 'Espera 30s entre ejecuciones.' : 'Wait 30s between runs.'); return; }
      setLast(await r.json());
    } finally { setBusy(false); }
  };
  return (
    <Card className="p-5 mt-3" data-testid="health-regression">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
            <ShieldCheck className="w-4 h-4 text-[#F77F11]" /> Regression Suite
            {last && (
              <span className={`rounded-full border px-2 py-0.5 text-[10.5px] font-bold ${last.verdict === 'PASS' ? REG_CHIP.pass : REG_CHIP.fail}`} data-testid="regression-verdict">
                {last.verdict}
              </span>
            )}
          </p>
          <p className="text-[12px] text-gray-400 mt-0.5">
            {isEs ? 'Protocolo KITXENS: ningún despliegue está terminado hasta que esta suite pase. Backend · Frontend · OAuth · Google · Vault · Intelligence · Brief · Rachel · Event Bus · Webhooks · Analytics · Pixel · Uploads · DB · Email.'
              : 'KITXENS Protocol: no deployment is complete until this suite passes. Backend · Frontend · OAuth · Google · Vault · Intelligence · Brief · Rachel · Event Bus · Webhooks · Analytics · Pixel · Uploads · DB · Email.'}
            {last && <> · {isEs ? 'Última' : 'Last'}: {timeAgo(last.at, isEs)} · {last.took_ms} ms · {last.passed}/{last.total} pass</>}
          </p>
        </div>
        <button onClick={run} disabled={busy} data-testid="regression-run-btn"
          className="inline-flex items-center gap-2 rounded-full bg-gray-900 hover:bg-[#F77F11] text-white text-[12.5px] font-semibold px-4 py-2 transition-colors disabled:opacity-50">
          <PlayCircle className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> {busy ? (isEs ? 'Ejecutando…' : 'Running…') : (isEs ? 'Ejecutar Regresión' : 'Run Regression')}
        </button>
      </div>
      {last && (
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="regression-checks">
          {last.checks.map((c) => (
            <div key={c.id} title={c.detail} data-testid={`regression-check-${c.id}`}
              className={`rounded-lg border px-3 py-2 ${REG_CHIP[c.status] || REG_CHIP.guarded}`}>
              <p className="text-[12px] font-bold">{c.status === 'pass' ? '✓' : c.status === 'fail' ? '✕' : '🛡'} {isEs ? c.label_es : c.label_en}</p>
              {c.detail && <p className="text-[10.5px] opacity-75 truncate">{c.detail}</p>}
            </div>
          ))}
        </div>
      )}
      {!last && <p className="mt-3 text-[12.5px] text-gray-400">{isEs ? 'Aún no se ha ejecutado ninguna regresión.' : 'No regression run yet.'}</p>}
    </Card>
  );
}

function ModuleMatrix({ isEs }) {
  const [mods, setMods] = useState(null);
  useEffect(() => {
    authFetch('/os2/module-status').then((r) => r.json()).then((j) => setMods(j.modules)).catch(() => {});
  }, []);
  if (!mods) return null;
  return (
    <Card className="overflow-hidden mt-3" data-testid="health-module-matrix">
      <div className="px-4 py-3 border-b border-gray-200">
        <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">{isEs ? 'Matriz de validación por módulo' : 'Module validation matrix'}</p>
        <p className="text-[11px] text-gray-400 mt-0.5">{isEs ? 'El lenguaje operativo de Kitxens: Producción · Validado · Monitoreo · Owner' : 'The operational language of Kitxens: Production · Validated · Monitoring · Owner'}</p>
      </div>
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="text-left text-gray-400 uppercase tracking-wider text-[10px] border-b border-gray-100">
            <th className="px-4 py-2">{isEs ? 'Módulo' : 'Module'}</th>
            <th className="px-2 py-2">{isEs ? 'Producción' : 'Production'}</th>
            <th className="px-2 py-2">{isEs ? 'Validado' : 'Validated'}</th>
            <th className="px-2 py-2">{isEs ? 'Monitoreo' : 'Monitoring'}</th>
            <th className="px-2 py-2">Owner</th>
          </tr>
        </thead>
        <tbody>
          {mods.map((m) => (
            <tr key={m.module} className="border-b border-gray-50 last:border-0" data-testid={`module-row-${m.module.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <td className="px-4 py-2.5">
                <b className="text-gray-900">{m.module}</b>
                {m.note && <p className="text-[10.5px] text-gray-400 mt-0.5 max-w-md">{m.note}</p>}
              </td>
              <td className="px-2 py-2.5">{m.production ? '✅' : '—'}</td>
              <td className="px-2 py-2.5">{VAL_ICON[m.validated] || '🟡'} <span className="text-[10.5px] text-gray-400 capitalize">{m.validated}</span></td>
              <td className="px-2 py-2.5">{m.monitoring === 'active' ? '🟢' : '—'}</td>
              <td className="px-2 py-2.5 text-gray-600">{m.owner}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export default function SystemHealthPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const { data: d, err, reload } = useOs2('/system-health');

  useEffect(() => {
    const t = setInterval(reload, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="os2-system-health">
      <PageHead
        title={isEs ? 'Centro de Operaciones' : 'Operations Center'}
        question={isEs ? '¿Está todo funcionando? — la fuente única de verdad de toda la plataforma, en vivo.' : 'Is everything working? — the single source of truth for the whole platform, live.'}
      />
      <div className="-mt-3 mb-4"><RangeNotice isEs={isEs} windows={isEs ? 'estado en vivo (ahora mismo), no histórico' : 'live status (right now), not historical'} /></div>
      {err && <Card className="p-5 text-red-600 text-[13px]">{String(err)}</Card>}
      {!d && !err && <Card className="p-8 text-center text-gray-400 text-[13px]">{isEs ? 'Ejecutando chequeos en vivo…' : 'Running live checks…'}</Card>}
      {d && (
        <Fade>
          {/* Two scores, side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card className="p-5 flex flex-wrap items-start justify-between gap-4" data-testid="health-score-card">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">Production Readiness</p>
                <p className={`text-4xl font-bold tabular-nums ${scoreColor(d.readiness_score)}`} data-testid="health-score">{d.readiness_score}<span className="text-lg text-gray-300">/100</span></p>
                <p className="mt-1.5 text-[11px] text-gray-400">{isEs ? 'Salud técnica: DB, servicios, jobs, endpoints' : 'Technical health: DB, services, jobs, endpoints'}</p>
              </div>
              <div className="text-right">
                <p className="text-[11.5px] text-gray-400">{isEs ? 'Chequeo en vivo' : 'Live check'} · {d.took_ms} ms</p>
                <button onClick={reload} className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-gray-200 hover:border-[#F77F11] px-3 py-1.5 text-[12px] font-semibold text-gray-600 transition-colors" data-testid="health-refresh">
                  <RefreshCw className="w-3.5 h-3.5" /> {isEs ? 'Re-chequear ahora' : 'Re-check now'}
                </button>
                <p className="mt-1 text-[10.5px] text-gray-300">{isEs ? 'Auto-refresco cada 60s' : 'Auto-refresh every 60s'}</p>
              </div>
            </Card>
            {d.company_health && (
              <Card className="p-5" data-testid="health-company-card">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">{isEs ? 'Salud de la empresa' : 'Company health'}</p>
                    <p className={`text-4xl font-bold tabular-nums ${scoreColor(d.company_health.score)}`} data-testid="health-company-score">{d.company_health.score}<span className="text-lg text-gray-300">/100</span></p>
                    <p className="mt-1.5 text-[11px] text-gray-400">{isEs ? 'Señales de negocio: agentes, automatizaciones, Event Bus' : 'Business signals: agents, automations, Event Bus'}</p>
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    {(d.company_health.factors || []).length ? (
                      <ul className="space-y-1.5" data-testid="health-company-factors">
                        {d.company_health.factors.map((f, i) => (
                          <li key={i} className="flex items-center justify-between gap-3 text-[12px]">
                            <span className="text-gray-600">{f.label}</span>
                            <b className="tabular-nums text-amber-600">{f.impact}</b>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[12.5px] text-emerald-600 font-semibold">✓ {isEs ? 'Sin señales negativas' : 'No negative signals'}</p>
                    )}
                  </div>
                </div>
              </Card>
            )}
          </div>
          {d.company_health && (
            <Card className="p-4 mt-3" data-testid="health-scores-explainer-card">
              <p className="text-[11.5px] leading-relaxed text-gray-400" data-testid="health-scores-explainer">
                {isEs
                  ? <><b className="text-gray-600">¿Por qué dos puntuaciones?</b> <b>Production Readiness ({d.readiness_score})</b> mide la salud técnica de la plataforma: base de datos, servicios, jobs y endpoints. <b>Salud de la empresa ({d.company_health.score})</b> mide señales de negocio: agentes AI activos, automatizaciones sin errores, entrega del Event Bus y webhook de Brevo. Pueden diferir: la plataforma puede estar perfecta técnicamente mientras un agente lleva 48h sin actividad.</>
                  : <><b className="text-gray-600">Why two scores?</b> <b>Production Readiness ({d.readiness_score})</b> measures platform technical health: database, services, jobs and endpoints. <b>Company health ({d.company_health.score})</b> measures business signals: active AI agents, error-free automations, Event Bus delivery and the Brevo webhook. They can differ: the platform can be technically perfect while an agent has been inactive for 48h.</>}
              </p>
            </Card>
          )}

          {/* Eva COO Report + Regression Suite + Preflight Protocol + Module validation matrix */}
          <EvaCooSection isEs={isEs} />
          <CrmSyncSection isEs={isEs} />
          <OpsSection isEs={isEs} />
          <RegressionSection isEs={isEs} />
          <PreflightSection isEs={isEs} />
          <ModuleMatrix isEs={isEs} />

          {/* Services */}
          <SectionTitle icon={Zap}>{isEs ? 'Servicios conectados' : 'Connected services'}</SectionTitle>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {d.services.map((s) => (
              <Card key={s.id} className="p-4" data-testid={`health-service-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[13px] font-bold text-gray-900 leading-snug">{s.name}</p>
                  <Pill status={s.status} />
                </div>
                <div className="mt-2 space-y-0.5 text-[11.5px] text-gray-500">
                  {s.latency_ms != null && <p>Latency: <b className="tabular-nums">{s.latency_ms} ms</b></p>}
                  {s.mode && <p>Mode: <b className={s.mode === 'live' ? 'text-emerald-600' : ''}>{s.mode}</b></p>}
                  {s.account && <p className="truncate">Account: <b>{s.account}</b></p>}
                  {s.events_24h != null && <p>{isEs ? 'Eventos 24h' : 'Events 24h'}: <b className="tabular-nums">{s.events_24h}</b></p>}
                  {s.last_event && <p className="truncate">{isEs ? 'Último evento' : 'Last event'}: <b>{s.last_event}</b> · {timeAgo(s.last_event_at, isEs)}</p>}
                  {s.last_success_at && <p>{isEs ? 'Última entrega OK' : 'Last success'}: <b>{timeAgo(s.last_success_at, isEs)}</b> ({s.last_success_event})</p>}
                  {s.last_payment_at && <p>{isEs ? 'Último pago' : 'Last payment'}: <b>{timeAgo(s.last_payment_at, isEs)}</b></p>}
                  {s.last_message_at && <p>{isEs ? 'Último email' : 'Last email'}: <b>{timeAgo(s.last_message_at, isEs)}</b></p>}
                  {s.failed_24h > 0 && <p className="text-amber-600">{isEs ? 'Fallos 24h' : 'Failures 24h'}: <b>{s.failed_24h}</b></p>}
                  {s.delivery && (
                    <p data-testid="health-delivery-failure-rate">{isEs ? 'Tasa de fallo (entrega)' : 'Delivery failure rate'}: <b className={`tabular-nums ${s.delivery.failure_rate_pct > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{s.delivery.failure_rate_pct}%</b> ({s.delivery.failed}/{s.delivery.total} {isEs ? 'desde' : 'since'} {s.delivery.since?.slice(0, 10)})</p>
                  )}
                  {s.last_error && <p className="text-red-500 truncate" title={s.last_error}>⚠ {s.last_error}</p>}
                  {s.error && <p className="text-red-500 truncate" title={s.error}>⚠ {s.error}</p>}
                </div>
              </Card>
            ))}
          </div>

          {/* Background jobs */}
          <SectionTitle icon={ListChecks}>{isEs ? 'Trabajos en segundo plano' : 'Background jobs'} <span className="text-gray-300 normal-case">— {d.background_jobs.online}/{d.background_jobs.total} online</span></SectionTitle>
          <Card className="divide-y divide-gray-50" data-testid="health-jobs">
            {d.background_jobs.jobs.map((j) => (
              <div key={j.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                <div className="flex-1 min-w-[220px]">
                  <p className="text-[13px] font-semibold text-gray-800">{j.id}</p>
                  <p className="text-[11.5px] text-gray-400">{j.purpose} · {j.schedule}</p>
                </div>
                <p className="text-[11.5px] text-gray-400 tabular-nums">{j.last_run_at ? timeAgo(j.last_run_at, isEs) : '—'}</p>
                <Pill status={j.status} />
              </div>
            ))}
          </Card>

          {/* Agents */}
          <SectionTitle icon={Bot}>{isEs ? 'Estado de los agentes AI' : 'AI agents status'}</SectionTitle>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(d.agents).map(([name, a]) => (
              <Card key={name} className="p-4" data-testid={`health-agent-${name}`}>
                <div className="flex items-center justify-between">
                  <p className="text-[13.5px] font-bold capitalize text-gray-900">{name}</p>
                  <Pill status={a.status} />
                </div>
                <p className="mt-1 text-[11px] text-gray-400 leading-snug">{a.role}</p>
                <p className="mt-2 text-[11.5px] text-gray-500">{isEs ? 'Eventos 7d' : 'Events 7d'}: <b className="tabular-nums">{a.events_7d}</b>
                  {a.last_activity_at && <> · {isEs ? 'última actividad' : 'last activity'} {timeAgo(a.last_activity_at, isEs)}</>}</p>
                {a.last_briefing && <p className="text-[11px] text-gray-400">{isEs ? 'Último briefing' : 'Last briefing'}: {a.last_briefing}</p>}
                {a.drafts_pending != null && <p className="text-[11px] text-gray-400">{isEs ? 'Borradores pendientes' : 'Drafts pending'}: {a.drafts_pending}</p>}
              </Card>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-3 mt-6">
            {/* Queues */}
            <Card className="p-4" data-testid="health-queues">
              <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 font-bold"><Landmark className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Colas pendientes' : 'Pending queues'}</p>
              <div className="mt-2 space-y-1 text-[12.5px] text-gray-600">
                <p>Event Bus {isEs ? 'pendientes' : 'pending'}: <b className="tabular-nums">{d.queues.event_bus_pending}</b> · {isEs ? 'fallidos' : 'failed'}: <b className={`tabular-nums ${d.queues.event_bus_failed > 0 ? 'text-amber-600' : ''}`}>{d.queues.event_bus_failed}</b></p>
                <p>{isEs ? 'Borradores de Penny por revisar' : 'Penny drafts awaiting review'}: <b className="tabular-nums">{d.queues.penny_drafts_awaiting_review}</b></p>
                <p>{isEs ? 'Payouts de partners pendientes' : 'Partner payouts pending'}: <b className="tabular-nums">{d.queues.affiliate_payouts_pending.count}</b> (${d.queues.affiliate_payouts_pending.amount})</p>
              </div>
            </Card>
            {/* Webhooks */}
            <Card className="p-4" data-testid="health-webhooks">
              <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 font-bold"><Webhook className="w-4 h-4 text-[#F77F11]" /> Webhooks</p>
              <div className="mt-2 space-y-1 text-[12.5px] text-gray-600">
                <p>Stripe: {d.webhooks.stripe.secret_set ? '🟢 secret set' : '⚪ no secret'}{d.webhooks.stripe.last_received && <> · {isEs ? 'último' : 'last'} {timeAgo(d.webhooks.stripe.last_received, isEs)}</>}</p>
                <p>Brevo: <Pill status={d.webhooks.brevo.status === 'ok' ? 'ok' : d.webhooks.brevo.status || 'unknown'} />{d.webhooks.brevo.last_event_at && <> · {timeAgo(d.webhooks.brevo.last_event_at, isEs)}</>}</p>
              </div>
            </Card>
          </div>

          {/* Database + analytics */}
          <div className="grid lg:grid-cols-2 gap-3 mt-3">
            <Card className="p-4" data-testid="health-database">
              <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 font-bold"><Database className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Base de datos' : 'Database'}</p>
              <div className="mt-2 grid grid-cols-3 gap-x-4 gap-y-1 text-[11.5px] text-gray-500">
                {Object.entries(d.services.find((s) => s.id === 'database')?.collections || {}).map(([c, n]) => (
                  <p key={c} className="truncate">{c}: <b className="tabular-nums text-gray-700">{n}</b></p>
                ))}
              </div>
            </Card>
            <Card className="p-4" data-testid="health-analytics">
              <p className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500 font-bold"><BarChart2 className="w-4 h-4 text-[#F77F11]" /> Analytics</p>
              <div className="mt-2 space-y-1.5 text-[12.5px] text-gray-600">
                <p data-testid="analytics-ga4-row">GA4: <Pill status={d.analytics.ga4.status} /> {d.analytics.ga4.id && <span className="font-mono text-[11px]">{d.analytics.ga4.id}</span>}
                  <span className="block text-[10.5px] text-gray-400">{d.analytics.ga4.note}</span></p>
                {d.analytics.ga4.reporting && (
                  <p data-testid="analytics-ga4-reporting-row">{isEs ? 'GA4 Reporting API' : 'GA4 Reporting API'}: <Pill status={d.analytics.ga4.reporting.status} />
                    <span className="block text-[10.5px] text-gray-400">{d.analytics.ga4.reporting.note}{d.analytics.ga4.reporting.last_success_at && <> · {isEs ? 'último dato' : 'last data'} {timeAgo(d.analytics.ga4.reporting.last_success_at, isEs)}</>}</span></p>
                )}
                {d.analytics.clarity && (
                  <p data-testid="analytics-clarity-row">Microsoft Clarity: <Pill status={d.analytics.clarity.status} /> {d.analytics.clarity.id && <span className="font-mono text-[11px]">{d.analytics.clarity.id}</span>}
                    <span className="block text-[10.5px] text-gray-400">{d.analytics.clarity.note}</span></p>
                )}
                {d.analytics.first_party && (
                  <p data-testid="analytics-firstparty-row">{isEs ? 'Píxel first-party' : 'First-party pixel'}: <Pill status={d.analytics.first_party.status} />
                    <span className="text-[11px]"> {d.analytics.first_party.events_24h} {isEs ? 'eventos 24h' : 'events 24h'}{d.analytics.first_party.last_event_at && <> · {isEs ? 'último' : 'last'} {timeAgo(d.analytics.first_party.last_event_at, isEs)}</>}</span></p>
                )}
                <p data-testid="analytics-gtm-row">Google Tag Manager: <Pill status={d.analytics.gtm.status} /> {d.analytics.gtm.id && <span className="font-mono text-[11px]">{d.analytics.gtm.id}</span>}</p>
                <p data-testid="analytics-gsc-row">Search Console: <Pill status={d.analytics.search_console.status || 'not_configured'} /> <span className="text-[11px] text-gray-400">{d.analytics.search_console.note}{d.analytics.search_console.last_success_at && <> · {isEs ? 'último dato' : 'last data'} {timeAgo(d.analytics.search_console.last_success_at, isEs)}</>}</span></p>
              </div>
            </Card>
          </div>

          {/* Source of truth */}
          <SectionTitle icon={Database}>{isEs ? 'Fuente única de verdad por dato' : 'Single source of truth per dataset'}</SectionTitle>
          <Card className="p-4" data-testid="health-ssot">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[12.5px]">
              {Object.entries(d.source_of_truth).map(([k, v]) => (
                <p key={k} className="text-gray-600"><b className="text-gray-800 capitalize">{k.replace(/_/g, ' ')}</b> → {v}</p>
              ))}
            </div>
          </Card>
          <p className="mt-4 text-[11px] text-gray-300">{isEs ? 'Generado' : 'Generated'} {new Date(d.generated_at).toLocaleString()}</p>
          <AdminPixelsPanel isEs={isEs} />
        </Fade>
      )}
    </div>
  );
}
